import type { FC } from 'react';
import { useContext } from 'react';

import { dataSetToAttributes } from '../../utils';
import { PictureContext } from './PictureContext';
import type { PictureProps } from './types';

/**
 * Helper function to attach src/srcst AFTER its been rendered and attached to the real DOM. we do
 * this because safari eagerly loads imgs the moment src/srcset is attached since react uses js to
 * create the nodes, which will cause the browser to load images that it doesn't need. Modern
 * browseres don't even need src/srcset set on <img> so it does not affect img load times at all
 * since the SSR DOM still includes sources and browser still loads quickly.
 *
 * Related Links: https://bug-190031-attachments.webkit.org/attachment.cgi?id=456958
 * https://bugs.webkit.org/show_bug.cgi?id=190031 fix soon maybe:
 * https://github.com/WebKit/WebKit/commit/ebdff21ed51676ee81de66302692042d5bf8d6c4 current
 * workaround found here: https://github.com/facebook/react/issues/20682
 */
export const addSrcFactory = (src?: string, srcSet?: string) => {
  return (img: HTMLImageElement) => {
    if (img && src) {
      img.src = src;
    }

    if (img && srcSet) {
      img.srcset = srcSet;
    }
  };
};

export const Picture: FC<PictureProps> = ({
  altText,
  className,
  style,
  defaultSrc,
  imgClassName,
  imgSrcs,
  height,
  width,
  fetchPriority,
  dataset,
  isDraggable,
  handleSafariSourceSets,
}) => {
  const hasNoSources = !imgSrcs?.sources?.length;
  const pictureContext = useContext(PictureContext);

  return (
    <picture className={className} style={style}>
      {imgSrcs?.sources?.map((imgSrc, index) => (
        <source
          srcSet={imgSrc.url}
          type={imgSrc.type}
          key={`${imgSrc.type}${index}desktop`}
          sizes={imgSrc.sizes}
          media={imgSrc.media}
        />
      ))}
      <img
        ref={
          handleSafariSourceSets
            ? addSrcFactory(imgSrcs?.default ?? defaultSrc, imgSrcs?.defaultSrcSet)
            : undefined
        }
        alt={altText ?? ''} // default to an empty string to ensure always render an alt attribute
        className={imgClassName}
        sizes={imgSrcs?.defaultSizes}
        height={height}
        width={width}
        // Render with src + srcSets attribute set if there is no risk of Safari loading multiple images (which only happens
        // when both src and srcSet are set on an <img> tag) or if consumer has opted out of the workaround.
        src={hasNoSources || !handleSafariSourceSets ? imgSrcs?.default ?? defaultSrc : undefined}
        srcSet={hasNoSources || !handleSafariSourceSets ? imgSrcs?.defaultSrcSet : undefined}
        loading={pictureContext?.lazy ? 'lazy' : undefined}
        // @ts-ignore this attribute is chrome only and doesn't exist (as of November 2023) on typings yet because its so new
        // eslint-disable-next-line react/no-unknown-property
        fetchpriorty={fetchPriority}
        {...dataSetToAttributes(dataset)}
        draggable={isDraggable}
      />
    </picture>
  );
};
